// ============================================
// SUCCESS PAGE
// ============================================

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Clock, MessageSquare, MessageCircle, Plus } from 'lucide-react';
import { Layout } from '../../components/layout';
import { Card, CardContent, Button } from '../../components/ui';
import { useAuthStore, useFormStore } from '../../store';
import { ROUTES, SUPPORT_EMAIL } from '../../constants';
import styles from './ResultPages.module.css';

const steps = [
  {
    icon: <CheckCircle size={20} />,
    title: 'Payment Confirmed',
    description: 'Your ₹99 consultation fee has been received.',
    status: 'complete',
  },
  {
    icon: <Clock size={20} />,
    title: 'Analysis in Progress',
    description: 'Our experts are reviewing your requirements.',
    status: 'current',
  },
  {
    icon: <MessageSquare size={20} />,
    title: 'WhatsApp Delivery',
    description: 'You\'ll receive your personalized recommendation directly on WhatsApp within 24 hours.',
    status: 'upcoming',
  },
];

export function SuccessPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { markCompleted, startNewForm } = useFormStore();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN);
    }
    // Mark form as completed to clear storage for future requests
    markCompleted();
  }, [isAuthenticated, navigate, markCompleted]);

  const handleNewRequest = () => {
    startNewForm();
    navigate(ROUTES.HOME);
  };

  const handleContactSupport = () => {
    const subject = encodeURIComponent('Support Request - Device Recommendation');
    const body = encodeURIComponent(`Hi FL Smartech Team,\n\nI need help with my device recommendation request.\n\nEmail: ${user?.email || ''}\n\nMy query:\n\n`);
    window.open(`https://mail.google.com/mail/?view=cm&fs=1&to=${SUPPORT_EMAIL}&su=${subject}&body=${body}`, '_blank');
  };

  return (
    <Layout maxWidth="sm">
      <div className={styles.container}>
        {/* Success Icon */}
        <div className={styles.iconWrapper + ' ' + styles.success}>
          <CheckCircle size={48} strokeWidth={1.5} />
        </div>

        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>Payment Successful!</h1>
          <p className={styles.subtitle}>
            Thank you for trusting us with your device recommendation.
          </p>
        </div>

        {/* Progress Steps */}
        <Card variant="elevated">
          <CardContent>
            <h2 className={styles.sectionTitle}>What happens next?</h2>
            <div className={styles.timeline}>
              {steps.map((step, index) => (
                <div key={index} className={`${styles.timelineItem} ${styles[step.status]}`}>
                  <div className={styles.timelineIcon}>{step.icon}</div>
                  <div className={styles.timelineContent}>
                    <h3 className={styles.timelineTitle}>{step.title}</h3>
                    <p className={styles.timelineDescription}>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card variant="bordered" padding="md">
          <CardContent>
            <div className={styles.infoContent}>
              <MessageSquare size={24} className={styles.infoIconGreen} />
              <div>
                <h3 className={styles.infoTitle}>Check your WhatsApp</h3>
                <p className={styles.infoText}>
                  We'll send your personalized recommendation directly to your{' '}
                  <strong>WhatsApp</strong> number within 24 hours.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Support */}
        <div className={styles.supportSection}>
          <p className={styles.supportText}>
            Have questions? We're here to help.
          </p>
          <div className={styles.actionButtons}>
            <Button variant="secondary" onClick={handleContactSupport}>
              <MessageCircle size={18} />
              Contact Support
            </Button>
            <Button variant="ghost" onClick={handleNewRequest}>
              <Plus size={18} />
              New Recommendation Request
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
